export interface InjectLinkOptions {
    rel: string;
    href: string;
}

/**
 * `href`/`src` here are built from the `data-base` attribute AEM authors set on `<app-root>` (see
 * launcher.ts), not from end-user input — but CodeQL still flags writing DOM-derived text back into
 * a resource URL (js/xss-through-dom), and a future change to that data path could make it genuinely
 * attacker-controlled. Restricting to http(s) blocks the concrete exploit (a `javascript:`/`data:` URI
 * executing on injection) without breaking the legitimate cross-origin CDN case this launcher supports.
 */
const assertHttpUrl = (url: string): string => {
    const protocol = new URL(url, document.baseURI).protocol;
    if (protocol !== "http:" && protocol !== "https:") {
        throw new Error(`Refusing to inject resource with unsafe URL scheme: ${url}`);
    }
    return url;
};

/**
 * Appends a `<link>` to `document.head` (e.g. `rel="modulepreload"`).
 */
export const injectLink = ({ rel, href }: InjectLinkOptions): HTMLLinkElement => {
    const link = document.createElement("link");
    link.rel = rel;
    link.href = assertHttpUrl(href);
    document.head.appendChild(link);
    return link;
};

/**
 * Appends a stylesheet `<link rel="stylesheet">` to `document.head`.
 */
export const injectStyle = (href: string): HTMLLinkElement => injectLink({ rel: "stylesheet", href });

export interface InjectScriptOptions {
    /**
     * If true, inserts `<link rel="modulepreload" href="…">` in `document.head` before the script.
     */
    modulePreload?: boolean;
    /** Invoked when the script element fires `load` (before the returned promise resolves). */
    onLoad?: (event: Event) => void;
}

/**
 * Appends a module `<script type="module">` to `document.body`. Resolves after load, rejects on error.
 */
export const injectScript = (src: string, options?: InjectScriptOptions): void => {
    const useModulePreload = options?.modulePreload === true;

    if (useModulePreload) {
        injectLink({ rel: "modulepreload", href: src });
    }

    const script = document.createElement("script");
    script.type = "module";
    // Dynamically created scripts default to `async = true`, which would let this run as soon as
    // its own fetch finishes, independent of other injected scripts. Callers (see launcher.ts) rely
    // on injection order for correctness — e.g. zone.js must execute before the main bundle — so
    // this must be cleared before `src` is set.
    script.async = false;
    script.src = assertHttpUrl(src);

    script.onload = (event: Event) => {
        options?.onLoad?.(event);
    };

    document.body.appendChild(script);
};
